// src/pages/ContactUs.jsx
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";

export default function ContactUs() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null); // success / error

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus(null);

    try {
      const res = await fetch("https://interviewnotebook.com/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (!res.ok) throw new Error("Failed to send");

      const data = await res.json();
      setStatus({ type: "success", message: "✅ Message sent successfully!" });

      // reset form
      setFormData({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      setStatus({
        type: "error",
        message: "❌ Failed to send message. Please try again later.",
      });
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Floating particles
  const [particles, setParticles] = useState([]);
  useEffect(() => {
    const temp = Array.from({ length: 20 }, (_, i) => ({
      id: i,
      x: Math.random() * window.innerWidth,
      y: Math.random() * window.innerHeight,
      size: 2 + Math.random() * 6,
      speed: 0.2 + Math.random() * 0.5,
    }));
    setParticles(temp);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setParticles((prev) =>
        prev.map((p) => ({
          ...p,
          y: p.y - p.speed < 0 ? window.innerHeight : p.y - p.speed,
        }))
      );
    }, 30);
    return () => clearInterval(interval);
  }, []);

  // Floating icons
  const icons = ["✉️", "💬", "📄"];
  const [floatingIcons, setFloatingIcons] = useState([]);
  useEffect(() => {
    const temp = Array.from({ length: 10 }, (_, i) => ({
      id: i,
      icon: icons[i % icons.length],
      x: Math.random() * window.innerWidth,
      y: Math.random() * window.innerHeight,
      speedX: (Math.random() - 0.5) * 0.5,
      speedY: (Math.random() - 0.5) * 0.3,
      size: 24 + Math.random() * 12,
    }));
    setFloatingIcons(temp);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setFloatingIcons((prev) =>
        prev.map((f) => ({
          ...f,
          x: (f.x + f.speedX + window.innerWidth) % window.innerWidth,
          y: (f.y + f.speedY + window.innerHeight) % window.innerHeight,
        }))
      );
    }, 50);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 flex flex-col p-6 overflow-hidden">
      {/* Floating Particles */}
      {particles.map((p) => (
        <motion.div
          key={p.id}
          className="absolute bg-white rounded-full opacity-30"
          style={{
            width: p.size,
            height: p.size,
            left: p.x,
            top: p.y,
          }}
        />
      ))}

      {/* Floating Icons */}
      {floatingIcons.map((f) => (
        <motion.div
          key={f.id}
          className="absolute text-2xl opacity-70"
          style={{
            left: f.x,
            top: f.y,
            fontSize: f.size,
          }}
        >
          {f.icon}
        </motion.div>
      ))}

      {/* Back Button */}
      <button
        onClick={() => navigate("/")}
        className="mb-6 bg-white text-indigo-600 px-4 py-2 rounded-lg shadow hover:bg-gray-100 transition w-max z-10 relative"
      >
        ← Back to Landing Page
      </button>

      <h1 className="text-4xl font-bold mb-12 text-white text-center z-10 relative">
        Contact Us
      </h1>

      <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto z-10 relative">
        {/* Contact Form */}
        <form
          onSubmit={handleSubmit}
          className="bg-white bg-opacity-90 p-8 rounded-2xl shadow-xl space-y-4 backdrop-blur-md"
        >
          <div>
            <label className="block font-semibold mb-1">Name</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block font-semibold mb-1">Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block font-semibold mb-1">Subject</label>
            <input
              type="text"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block font-semibold mb-1">Message</label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              required
              rows={5}
              className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className={`${
              loading ? "bg-gray-400" : "bg-indigo-600 hover:bg-indigo-700"
            } text-white font-semibold px-6 py-3 rounded-lg shadow transition w-full`}
          >
            {loading ? "Sending..." : "Send Message"}
          </button>

          {status && (
            <p
              className={`mt-2 text-center font-medium ${
                status.type === "success" ? "text-green-600" : "text-red-600"
              }`}
            >
              {status.message}
            </p>
          )}
        </form>

        {/* Map & Social Links */}
        <div className="space-y-6">
          {/* Map Embed */}
          <div className="w-full h-64 rounded-2xl overflow-hidden shadow-xl">
            <iframe
              src="https://www.openstreetmap.org/export/embed.html?bbox=77.5946%2C12.9716%2C77.5946%2C12.9716&layer=mapnik"
              className="w-full h-full border-0"
              title="Map"
            ></iframe>
          </div>

          {/* Social Links */}
          <div className="bg-white bg-opacity-90 p-6 rounded-2xl shadow-xl backdrop-blur-md">
            <h2 className="text-xl font-semibold mb-4 text-gray-800">
              Connect with us
            </h2>
            <div className="flex space-x-6">
              <a
                href="https://twitter.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-500 hover:underline"
              >
                Twitter
              </a>
              <a
                href="https://linkedin.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-700 hover:underline"
              >
                LinkedIn
              </a>
              <a
                href="https://github.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-800 hover:underline"
              >
                GitHub
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
